import { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Box } from '@mui/material';

import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProfileDetection } from './components/ProfileDetection';
import { createLazyComponent } from './utils/lazy-loading';
import { trackWebVitals, logMemoryUsage } from './utils/performance';

// Lazy load pages for better performance
const LandingPage = createLazyComponent(
  () => import('./pages/LandingPage').then(m => ({ default: m.LandingPage }))
);

const PricingPage = createLazyComponent(
  () => import('./pages/PricingPage').then(m => ({ default: m.PricingPage }))
);

const AboutPage = createLazyComponent(
  () => import('./pages/AboutPage').then(m => ({ default: m.AboutPage }))
);

const LoginPage = createLazyComponent(
  () => import('./pages/LoginPage').then(m => ({ default: m.LoginPage }))
);

const DashboardPage = createLazyComponent(
  () => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage }))
);

const NotFoundPage = createLazyComponent(
  () => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage }))
);

const ForgotPasswordPage = createLazyComponent(
  () => import('./pages/ForgotPasswordPage')
);

const ResetPasswordPage = createLazyComponent(
  () => import('./pages/ResetPasswordPage')
);

const VerifyEmailPage = createLazyComponent(
  () => import('./pages/VerifyEmailPage')
);

const TermsPage = createLazyComponent(
  () => import('./pages/TermsPage').then(m => ({ default: m.TermsPage }))
);

const PrivacyPage = createLazyComponent(
  () => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage }))
);

// Lazy load onboarding workflow
const OnboardingWorkflow = createLazyComponent(
  () => import('./components/onboarding/OnboardingWorkflow').then(m => ({ default: m.OnboardingWorkflow }))
);

function App() {
  useEffect(() => {
    // Initialize performance monitoring
    trackWebVitals();

    // Log memory usage in development
    if (process.env.NODE_ENV === 'development') {
      const interval = setInterval(logMemoryUsage, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }

    // Return undefined for the case where no cleanup is needed
    return undefined;
  }, []);

  return (
    <AuthProvider>
      <NotificationProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route element={<Layout />}>
              <Route path="pricing" element={<PricingPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
              <Route path="verify-email" element={<VerifyEmailPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            {/* 
              MVP RESTRICTION: Onboarding route - one profile per user
              
              Wrapping with ProfileDetection (redirectIfHasProfile=true) prevents users
              who already have a profile from accessing onboarding again, enforcing the
              MVP limit of one creator profile per user.
              
              FUTURE: When implementing multi-profile support, either:
              1. Remove ProfileDetection wrapper to allow re-access, OR
              2. Change onboarding to a "create new profile" flow that lists existing profiles
              
              The underlying data model already supports multiple profiles per user.
            */}
            <Route
              path="onboarding"
              element={
                <ProtectedRoute>
                  <ProfileDetection key="onboarding" redirectIfHasProfile={true}>
                    <OnboardingWorkflow />
                  </ProfileDetection>
                </ProtectedRoute>
              }
            />
            {/* Dashboard route - requires authentication AND creator profile */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute>
                  <ProfileDetection key="dashboard">
                    <DashboardPage />
                  </ProfileDetection>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Box>
      </NotificationProvider >
    </AuthProvider >
  );
}

export default App;